import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import moment from "moment";
import KpiStatCard from "../components/KpiStatCard";
import TransferMachineryModal from "../components/TransferMachineryModal";
import SiteSearchSelector from "../components/SiteSearchSelector";
import { safeText } from "../utils/hubHelpers";

export default function MachineryTransfers() {
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [activeTransferDetail, setActiveTransferDetail] = useState(null);

  useEffect(() => {
    fetchSites();
    fetchTransfers();
  }, [selectedSiteId]);

  const fetchSites = async () => {
    try {
      const res = await callApi(apiUrl.get_site, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.site)
          ? res.data.site
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setSites(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      let query = `?pageSize=100`;
      if (selectedSiteId) query += `&siteId=${selectedSiteId}`;
      if (searchQuery) query += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await callApi(`${apiUrl.get_transfer_asset}${query}`, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.transferAsset)
          ? res.data.transferAsset
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setTransfers(list);
      } else {
        setTransfers([]);
      }
    } catch (e) {
      console.error(e);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const safeTransfers = Array.isArray(transfers) ? transfers : [];

  const filteredTransfers = safeTransfers.filter((t) => {
    if (!t) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const ta = (t.taNumber || "").toLowerCase();
      const from = (t.fromSite?.name || "").toLowerCase();
      const to = (t.toSite?.name || "").toLowerCase();
      const desc = (t.description || "").toLowerCase();
      if (!ta.includes(q) && !from.includes(q) && !to.includes(q) && !desc.includes(q)) return false;
    }
    return true;
  });

  const totalTransfers = safeTransfers.length;
  const recent30Days = safeTransfers.filter((t) =>
    moment(t.date || t.createdAt).isAfter(moment().subtract(30, "days"))
  ).length;

  return (
    <div>
      {/* 1. Transfer KPI Metrics */}
      <div className="kpi-grid">
        <KpiStatCard
          label="Total Relocations"
          value={totalTransfers}
          subtext="Recorded inter-site transfers"
          icon={<span>🔁</span>}
          color="#3b82f6"
          iconBg="#eff6ff"
        />

        <KpiStatCard
          label="Active Last 30 Days"
          value={recent30Days}
          subtext="Recent machinery movements"
          icon={<span>🚚</span>}
          color="#10b981"
          iconBg="#ecfdf5"
        />

        <KpiStatCard
          label="Active Destination Sites"
          value={new Set(safeTransfers.map((t) => t.toSiteId || t.toSite?.id)).size}
          subtext="Receiving solar & project locations"
          icon={<span>📍</span>}
          color="#8b5cf6"
          iconBg="#f5f3ff"
        />

        <KpiStatCard
          label="Gate Pass & TA Records"
          value={totalTransfers}
          subtext="100% compliant documentation"
          icon={<span>📄</span>}
          color="#059669"
          iconBg="#f0fdf4"
        />
      </div>

      {/* 2. Searchable Site Selector */}
      {(() => {
        const countsMap = sites.reduce((acc, site) => {
          const sId = site?.id || "";
          acc[sId] = safeTransfers.filter(
            (t) => t.fromSiteId === sId || t.toSiteId === sId || t.fromSite?.id === sId || t.toSite?.id === sId
          ).length;
          return acc;
        }, {});

        return (
          <SiteSearchSelector
            sites={sites}
            selectedSiteId={selectedSiteId}
            onSelectSite={setSelectedSiteId}
            countsMap={countsMap}
            totalCount={safeTransfers.length}
          />
        );
      })()}

      {/* 3. Toolbar */}
      <div className="hub-toolbar">
        <div className="toolbar-left">
          <div className="search-box-wrap">
            <span className="search-icon-pos">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search TA number, origin or destination site, notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchTransfers()}
            />
          </div>
        </div>

        <div className="toolbar-right">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsTransferModalOpen(true)}
            style={{
              background: "#2563eb",
              borderColor: "#2563eb",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span>+</span> Initiate Machinery Transfer
          </button>
        </div>
      </div>

      {/* 4. Transfers Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status" style={{ marginBottom: "1rem" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Loading Transfer Records...</p>
        </div>
      ) : filteredTransfers.length === 0 ? (
        <div
          style={{
            background: "#ffffff",
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            padding: "4rem 2rem",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "2.5rem", display: "block", marginBottom: "0.5rem" }}>🚚</span>
          <h4 style={{ color: "#0f172a", fontWeight: 700 }}>No Transfer Records Found</h4>
          <p style={{ color: "#64748b", maxWidth: 420, margin: "0 auto 1.5rem auto", fontSize: "0.875rem" }}>
            No inter-site equipment transfers match your filter. Initiate a transfer to move machinery between sites.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsTransferModalOpen(true)}
          >
            + Transfer Machinery
          </button>
        </div>
      ) : (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", textTransform: "uppercase", fontSize: "11.5px", letterSpacing: "0.04em" }}>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>TA Voucher #</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Transfer Date</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Origin Site (From)</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Destination Site (To)</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Transfer Notes / Equipment</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Initiated By</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "right", fontWeight: 700 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransfers.map((item) => (
                <tr key={item.id || Math.random()} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                    <span style={{ fontWeight: 700, color: "#2563eb", fontFamily: "monospace", fontSize: "13px" }}>
                      {safeText(item.taNumber, `TA-${item.id}`)}
                    </span>
                  </td>
                  <td style={{ padding: "0.85rem 1rem", color: "#334155", fontWeight: 600, fontSize: "13px", textAlign: "left" }}>
                    {item.date ? moment(item.date).format("DD MMM YYYY") : moment(item.createdAt).format("DD MMM YYYY")}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", fontSize: "13px", textAlign: "left" }}>
                    <div style={{ fontWeight: 600, color: "#0f172a", textAlign: "left" }}>
                      📍 {safeText(item.fromSite?.name || item.fromSite, "Origin Site")}
                    </div>
                  </td>
                  <td style={{ padding: "0.85rem 1rem", fontSize: "13px", textAlign: "left" }}>
                    <div style={{ fontWeight: 600, color: "#059669", textAlign: "left" }}>
                      ➔ 📍 {safeText(item.toSite?.name || item.toSite, "Destination Site")}
                    </div>
                  </td>
                  <td style={{ padding: "0.85rem 1rem", color: "#475569", maxWidth: 280, textAlign: "left" }}>
                    <div style={{ fontSize: "12.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }} title={item.description}>
                      {safeText(item.description, "Machinery Relocation")}
                    </div>
                  </td>
                  <td style={{ padding: "0.85rem 1rem", color: "#64748b", fontSize: "13px", textAlign: "left" }}>
                    {safeText(item.user?.name, "Operations")}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", textAlign: "right" }}>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: 12,
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#059669",
                        background: "#dcfce7",
                        display: "inline-block",
                      }}
                    >
                      ✓ Completed
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer Modal */}
      <TransferMachineryModal
        isOpen={isTransferModalOpen}
        toggle={() => setIsTransferModalOpen(!isTransferModalOpen)}
        onSuccess={fetchTransfers}
      />
    </div>
  );
}
